import { Injectable } from '@angular/core';
import { VIDEOS_AND_EVENTS_CATEGORIES } from 'src/app/modules/shared/enums/videos-and-events.enum';
import { VideosAndEventsService } from 'src/app/services/videos-and-events/videos-and-events.service';
import { IVideo } from '../../interfaces/videosByCategory';
import { BehaviorSubject } from 'rxjs';
import { IVideoResponseModel } from 'src/app/services/videos-and-events/models/videos-and-events-model';

@Injectable({
  providedIn: 'root'
})
export class LiveTransmissionsService {
  private liveDataSubject = new BehaviorSubject<{
    viewMode: 'loading' | 'ready';
    items: IVideo[];
    showLiveTransmissionsCategory: boolean;
  }>({
    viewMode: 'loading',
    items: [],
    showLiveTransmissionsCategory: false
  });

  liveData$ = this.liveDataSubject.asObservable();

  constructor(private videosAndEventsService: VideosAndEventsService) {
    this.loadLives();
  }

  loadLives(): void {
    this.videosAndEventsService
      .getVideosWithQuery({
        page: 1,
        pageSize: 20,
        categoryId: VIDEOS_AND_EVENTS_CATEGORIES.LIVE_TRANSMISSIONS,
        orderBy: 'data DESC'
      })
      .result$.subscribe({
        next: (response) => {
          const data = response?.data;
          if (!data) return;

          const items = data.items.map((video) => this.formatVideo(video));
          const sorted = [...items].sort((a, b) => this.compareLiveVideos(a, b));

          this.liveDataSubject.next({
            viewMode: 'ready',
            items: sorted,
            showLiveTransmissionsCategory: sorted.length > 0
          });
        }
      });
  }

  private formatVideo(video: IVideoResponseModel): IVideo {
    return {
      id: video.id,
      title: video.titulo,
      subtitle: video.resumo,
      videoURL: video.conteudo,
      categoryId: video.categoria.id,
      imagem: video.imagem,
      imagemMobile: video.imagemMobile,
      data: video.data,
      statusTransmissaoValue: video.statusTransmissaoValue,
      dateTimeTransmissao: video.dateTimeTransmissao
    };
  }

  private compareLiveVideos(a: IVideo, b: IVideo): number {
    const priorityA = this.getVideoPriority(a);
    const priorityB = this.getVideoPriority(b);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    if (priorityA === 2) {
      return this.getTransmissionTime(b) - this.getTransmissionTime(a);
    }

    return this.getTransmissionTime(a) - this.getTransmissionTime(b);
  }

  private getVideoPriority(video: IVideo): number {
    const status = this.normalizeStatus(video.statusTransmissaoValue);

    if (status === 'ao_vivo') {
      return 0;
    }

    if (status === 'agendada' || status === 'em_breve') {
      return 1;
    }

    const transmissionTime = this.getTransmissionTime(video);
    return transmissionTime > Date.now() ? 1 : 2;
  }

  private normalizeStatus(status?: string | null): string | null {
    return status?.trim().toLowerCase().replace(/\s+/g, '_') ?? null;
  }

  private getTransmissionTime(video: IVideo): number {
    if (video.dateTimeTransmissao) {
      const time = new Date(video.dateTimeTransmissao).getTime();
      return Number.isNaN(time) ? 0 : time;
    }

    return video.data?.rawValue ?? 0;
  }
}
